// AI Tutor streaming presentation (doc 27, ST-01) -- reveal units.
//
// Splits a tutor reply's Markdown SOURCE into the steps it is revealed in.
// Deterministic and dependency-free on purpose (doc 27 §7, §28.5): no
// `Intl.Segmenter`, so every browser reveals the same text the same way.
//
//   prose            one unit per whitespace-delimited word, with its
//                    punctuation and the whitespace after it. U+200C (ZWNJ) is
//                    not whitespace, so «می‌خوانم» stays one word.
//   atomic spans     inline code, inline and display math, links, fenced
//                    blocks (figures included) and tables are never cut: a
//                    span is revealed whole, together with whatever touches it
//                    («($\Delta$)،» is one unit).
//   line markers     `- `, `1. `, `## `, `> ` appear with the first word of
//                    their line, never alone.
//
// A span that has opened but not closed yet is `open`: the reveal waits in
// front of it until the closer arrives. Once the text is complete an unclosed
// opener is ordinary text -- which is also what the final render shows.

const FENCE_OPEN = /^[ \t]{0,3}(`{3,}|~{3,})/
const TABLE_LINE = /^[ \t]{0,3}\|/
const LINE_MARKER = /^[ \t]{0,3}(?:(?:[-*+]|\d{1,9}[.)])[ \t]+|#{1,6}[ \t]+|>[ \t]?)+/
const SENTENCE_END = /[.!?؟…]["'»)\]]*$/
const LINK = /^!?\[(?:[^[\]\\]|\\.)*\]\((?:[^()\s\\]|\\.|\([^()\s]*\))*(?:[ \t]+"[^"\n]*")?\)/
const AUTOLINK = /^<(?:https?:\/\/|mailto:)[^<>\s]+>/i

function isSpace(ch) {
  return ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '\f'
}

function lineEnd(text, from) {
  const at = text.indexOf('\n', from)
  return at === -1 ? text.length : at + 1
}

// The paragraph an inline span must close in: Markdown inline content never
// crosses a blank line.
function paragraphEnd(text, from) {
  const match = /\n[ \t]*\n/.exec(text.slice(from))
  return match ? from + match.index : text.length
}

function closeFence(text, start, fence) {
  const char = fence[0]
  const close = new RegExp(`^[ \\t]{0,3}\\${char}{${fence.length},}[ \\t]*$`)
  let at = lineEnd(text, start)
  while (at < text.length) {
    const next = lineEnd(text, at)
    if (close.test(text.slice(at, next).replace(/\r?\n$/, ''))) return next
    at = next
  }
  return -1
}

// `$…$`: opened by `$` followed by a non-space, closed by `$` that follows a
// non-space and is not followed by a digit (markdown-it-texmath's own rule, so
// «$5 تا $10» is money, not a formula).
function closeInlineMath(text, start, limit) {
  let at = start + 1
  while (at < limit) {
    const ch = text[at]
    if (ch === '\\') {
      at += 2
      continue
    }
    if (
      ch === '$' &&
      !isSpace(text[at - 1]) &&
      !/[0-9]/.test(text[at + 1] || '')
    ) {
      return at + 1
    }
    at += 1
  }
  return -1
}

// An inline span with no closer yet: still arriving while its paragraph is the
// last one, ordinary text once the paragraph has ended without it.
function unclosed(text, at) {
  return paragraphEnd(text, at) >= text.length ? null : -1
}

/**
 * The end of the atomic span that opens at `at`, `-1` when nothing atomic
 * opens there, or `null` when one opens but has not closed yet.
 */
function atomicEnd(text, at) {
  const ch = text[at]
  if (ch === '`') {
    let run = 1
    while (text[at + run] === '`') run += 1
    const ticks = text.slice(at, at + run)
    let from = at + run
    const limit = paragraphEnd(text, at)
    while (from < limit) {
      const close = text.indexOf(ticks, from)
      if (close === -1 || close >= limit) break
      // a longer run of backticks is not this span's closer
      if (text[close + run] !== '`' && text[close - 1] !== '`')
        return close + run
      from = close + 1
      while (text[from] === '`') from += 1
    }
    return unclosed(text, at)
  }
  if (ch === '$') {
    if (text[at + 1] === '$') {
      const close = text.indexOf('$$', at + 2)
      return close === -1 ? null : close + 2
    }
    if (at + 1 >= text.length) return null
    if (isSpace(text[at + 1])) return -1
    const close = closeInlineMath(text, at, paragraphEnd(text, at))
    return close === -1 ? unclosed(text, at) : close
  }
  if (ch === '\\' && (text[at + 1] === '(' || text[at + 1] === '[')) {
    const closer = text[at + 1] === '(' ? '\\)' : '\\]'
    const close = text.indexOf(closer, at + 2)
    return close === -1 ? null : close + 2
  }
  if (ch === '[' || (ch === '!' && text[at + 1] === '[')) {
    const rest = text.slice(at, paragraphEnd(text, at))
    const link = LINK.exec(rest)
    if (link) return at + link[0].length
    // `[text](` with no `)` yet: the link is still arriving
    if (/^!?\[(?:[^[\]\\]|\\.)*\]\([^)\n]*$/.test(rest)) {
      return unclosed(text, at)
    }
    return -1
  }
  if (ch === '<') {
    const link = AUTOLINK.exec(text.slice(at, at + 2048))
    return link ? at + link[0].length : -1
  }
  return -1
}

/**
 * @typedef {Object} RevealUnit
 * @property {number} end    offset just after the unit (its trailing
 *                           whitespace included)
 * @property {boolean} pause a sentence or a paragraph ends here
 * @property {boolean} open  an atomic span in it has not closed yet: not
 *                           revealable
 */

/**
 * @param {string} text the Markdown source received so far
 * @param {{complete?: boolean}} [options] `complete`: no more text will
 *   arrive, so nothing is held back
 * @returns {RevealUnit[]}
 */
export function segmentReveal(text, options) {
  const source = typeof text === 'string' ? text : ''
  const complete = !!(options && options.complete)
  const units = []
  let at = 0

  const push = (end, flags) => {
    let stop = end
    while (stop < source.length && isSpace(source[stop])) stop += 1
    const body = source.slice(at, end)
    const gap = source.slice(end, stop)
    units.push({
      end: stop,
      pause:
        !!(flags && flags.block) ||
        SENTENCE_END.test(body) ||
        /\n[ \t]*\n/.test(gap),
      open: false,
    })
    at = stop
  }
  const hold = () => {
    units.push({ end: source.length, pause: false, open: true })
    at = source.length
  }

  // leading whitespace belongs to nothing: it goes with the first unit
  while (at < source.length) {
    let cursor = at
    while (cursor < source.length && isSpace(source[cursor])) cursor += 1
    if (cursor >= source.length) {
      if (units.length) units[units.length - 1].end = source.length
      else units.push({ end: source.length, pause: false, open: false })
      break
    }

    const atLineStart = cursor === 0 || source[cursor - 1] === '\n'
    const lineStart = atLineStart
      ? cursor
      : source.lastIndexOf('\n', cursor - 1) + 1
    const onOwnLine =
      atLineStart || /^[ \t]*$/.test(source.slice(lineStart, cursor))

    if (onOwnLine) {
      const line = source.slice(lineStart, lineEnd(source, lineStart))
      const fence = FENCE_OPEN.exec(line)
      if (fence) {
        const close = closeFence(source, lineStart, fence[1])
        if (close !== -1) {
          push(close, { block: true })
          continue
        }
        if (!complete) {
          hold()
          break
        }
      } else if (TABLE_LINE.test(line)) {
        let end = lineStart
        while (
          end < source.length &&
          TABLE_LINE.test(source.slice(end, lineEnd(source, end)))
        ) {
          end = lineEnd(source, end)
        }
        // A table that runs to the end of what has arrived may still grow.
        if (end >= source.length && !complete) {
          hold()
          break
        }
        push(end, { block: true })
        continue
      } else {
        const marker = LINE_MARKER.exec(line)
        if (marker) cursor = lineStart + marker[0].length
        while (cursor < source.length && isSpace(source[cursor])) {
          if (source[cursor] === '\n') break
          cursor += 1
        }
      }
    }

    // one word: everything up to the next whitespace, atomic spans swallowed
    let open = false
    while (cursor < source.length && !isSpace(source[cursor])) {
      const end = atomicEnd(source, cursor)
      if (end === null) {
        if (!complete) {
          open = true
          break
        }
        cursor += 1
      } else if (end === -1) {
        cursor += 1
      } else {
        cursor = end
      }
    }
    if (open) {
      hold()
      break
    }
    push(cursor)
  }
  return units
}

/**
 * How many units are waiting behind `shownEnd` (open ones are not waiting:
 * they cannot be shown yet).
 * @param {RevealUnit[]} units
 * @param {number} shownEnd
 */
export function countBacklog(units, shownEnd) {
  let count = 0
  for (let i = units.length - 1; i >= 0; i -= 1) {
    const unit = units[i]
    if (unit.end <= shownEnd) break
    if (!unit.open) count += 1
  }
  return count
}
